import { useState, useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  RefreshCw, Search, Download, Inbox, Calendar, Hash, Sparkles, Activity, Loader2,
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { loadWithCache, getCached } from '../lib/cache';
import { onOrdersChanged } from '../lib/ordersWatch';
import JSZip from 'jszip';
import { cn, formatNumber, formatDateTime } from '../lib/utils';
import { reportAlert } from '../lib/alertReporter';
import { Card } from './ui/Card';
import { Input } from './ui/Input';
import Button from './ui/Button';
import Tabs from './ui/Tabs';
import { SkeletonRow } from './ui/Skeleton';
import PageHeader from './ui/PageHeader';
import MetricCard from './ui/MetricCard';
import EmptyState from './ui/EmptyState';
import StatusBadge from './ui/StatusBadge';
import Badge from './ui/Badge';
import { MARKETPLACE_META } from './marketplaces';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const APLUS_FN_URL = `${SUPABASE_URL}/functions/v1/generate-aplus`;

const FILTER_TABS = [
  { id: 'all',    label: 'Wszystkie' },
  { id: 'active', label: 'Aktywne'   },
  { id: 'done',   label: 'Ukończone' },
  { id: 'error',  label: 'Z błędami' },
];

export default function HistoryPage({ user, tenantScope = null }) {
  // T2.3: konto klienta agencji nie widzi kredytów.
  const hideCredits = !!tenantScope?.tenantId && !tenantScope?.orgId;
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [aplusBusy, setAplusBusy] = useState(null);
  const [aplusProg, setAplusProg] = useState({});
  const [nowTs, setNowTs] = useState(Date.now());
  const aplusStart = useRef({});
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [gallery, setGallery] = useState(null); // { order, images, loading }
  const [missing, setMissing] = useState(null); // { order, rows, loading } — produkty bez treści (A2/A3)

  const openMissing = async (order) => {
    setMissing({ order, rows: [], loading: true });
    const { data, error } = await supabase.rpc('order_failed_products', { p_order_id: order.id });
    if (error) toast.error('Nie udało się pobrać listy: ' + error.message);
    setMissing({ order, rows: data || [], loading: false });
  };
  const [zipBusy, setZipBusy] = useState(false);

  const openGallery = async (order) => {
    setGallery({ order, images: [], loading: true });
    const { data } = await supabase
      .from('aplus_results')
      .select('row_index, panel, url')
      .eq('order_id', order.id)
      .order('row_index', { ascending: true })
      .order('panel', { ascending: true });
    setGallery({ order, images: data || [], loading: false });
  };

  const downloadZip = async (order, images) => {
    setZipBusy(true);
    try {
      const zip = new JSZip();
      await Promise.all(images.map(async (img) => {
        const r = await fetch(img.url);
        const b = await r.blob();
        zip.file(`produkt-${img.row_index + 1}_${img.panel || 'panel'}.png`, b);
      }));
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(order.filename || order.marketplace || 'order').replace(/\.xlsx$/i, '')}_A+.zip`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Pobrano paczkę A+ (ZIP).');
    } catch (e) {
      toast.error('Błąd pobierania ZIP: ' + e.message);
      reportAlert({ title: 'Pobieranie ZIP A+ nie powiodlo sie', message: e.message, details: 'order=' + (order && order.id) });
    } finally {
      setZipBusy(false);
    }
  };

  // PERF-UX (21.10): historia od razu z cache (ostatnie 500 zleceń), świeże w tle.
  const fetchOrders = async () => {
    const key = `history:orders:${tenantScope?.tenantId || tenantScope?.orgId || user.id}`;
    if (!getCached(key)) setLoading(true);
    try {
      await loadWithCache(key, async () => {
        // T2.3: konto klienta widzi zlecenia SWOJEGO klienta (nie tylko własne); zespół agencji — zlecenia klientów organizacji + własne.
        let query = supabase.from('orders').select('*').order('created_at', { ascending: false }).limit(500);
        if (tenantScope?.tenantId) query = query.eq('tenant_id', tenantScope.tenantId);
        else if (tenantScope?.orgId) {
          const { data: ts } = await supabase.from('tenants').select('id').eq('org_id', tenantScope.orgId);
          const ids = (ts || []).map(t => t.id);
          query = ids.length ? query.or(`user_id.eq.${user.id},tenant_id.in.(${ids.join(',')})`) : query.eq('user_id', user.id);
        } else query = query.eq('user_id', user.id);
        const { data, error } = await query;
        if (error) throw error;
        // K2: nazwa klienta przy zleceniu (admin widzi tenants; zwykły użytkownik dostanie pustą listę — bez błędu).
        const tenantIds = [...new Set((data || []).map(o => o.tenant_id).filter(Boolean))];
        let names = {};
        if (tenantIds.length) {
          const { data: t } = await supabase.from('tenants').select('id, name').in('id', tenantIds);
          names = Object.fromEntries((t || []).map(x => [x.id, x.name]));
        }
        return (data || []).map(o => ({ ...o, tenant_name: o.tenant_id ? names[o.tenant_id] || null : null }));
      }, (list) => { setOrders(list); setLoading(false); });
    } catch (e) { toast.error(e.message); setLoading(false); }
  };

  useEffect(() => { fetchOrders(); /* eslint-disable-next-line */ }, []);
  // Zlecenie uruchomione / zakończone gdziekolwiek w aplikacji → lista od razu świeża.
  useEffect(() => onOrdersChanged(() => fetchOrders()), []);

  const handleAplus = async (order) => {
    setAplusBusy(order.id);
    try {
      // Token zalogowanego usera, nie anon key: edge fn sprawdza wlasciciela zlecenia (D33).
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Sesja wygasla - zaloguj sie ponownie');

      const res = await fetch(APLUS_FN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ order_id: order.id, image_column: order.aplus_image_column, language: order.language }),
      });
      // T2.8: A+ jest płatny (kredyty pobiera edge, raz na zlecenie).
      if (res.status === 402) { const d = await res.json().catch(() => ({})); throw new Error(`za mało kredytów — potrzeba ${d.need ?? '?'}`); }
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || `Blad ${res.status}`); }
      toast.success('A+ Content: generowanie uruchomione (kredyty pobrane).');
      aplusStart.current[order.id] = Date.now();
      setAplusProg(prev => ({ ...prev, [order.id]: { done: 0, total: Math.min(order.product_count || 1, 5) * 3 } }));
      setNowTs(Date.now());
      setOrders(prev => prev.map(o => o.id === order.id ? { ...o, aplus_status: 'processing' } : o));
    } catch (err) {
      toast.error(`A+ blad: ${err.message}`);
      reportAlert({ title: 'A+ generowanie nie ruszylo', message: err.message, details: 'order=' + (order && order.id) });
    } finally {
      setAplusBusy(null);
    }
  };

  const ordersRef = useRef([]);
  useEffect(() => { ordersRef.current = orders; }, [orders]);

  // Miedzy przyjeciem zlecenia (202) a claimem taska (`aplus_status='processing'`) mija chwila:
  // przez ten czas baza nadal mowi `idle`. Bez okna laski poll cofnalby optymistyczny `processing`,
  // a wtedy petla pollujaca (warunek `some(...processing)`) gasnie i UI zastyga na `Generuj A+`
  // mimo poprawnie konczacego sie biegu. Grace = tyle, ile kolejka moze trzymac run przed startem.
  const APLUS_CLAIM_GRACE_MS = 3 * 60 * 1000;
  const awaitingClaim = (id) => {
    const t = aplusStart.current[id];
    return Boolean(t) && Date.now() - t < APLUS_CLAIM_GRACE_MS;
  };

  const refreshAplus = async () => {
    const proc = ordersRef.current.filter(
      o => (o.aplus_content || o.aplus_status === 'processing') && (o.aplus_status === 'processing' || awaitingClaim(o.id))
    );
    if (proc.length === 0) return;
    const ids = proc.map(o => o.id);
    const { data: ord } = await supabase.from('orders').select('id, aplus_status, product_count').in('id', ids);
    if (ord && ord.length) {
      setOrders(prev => prev.map(o => {
        const u = ord.find(x => x.id === o.id);
        if (!u) return o;
        // Task jeszcze nie przejal zlecenia — trzymamy optymistyczny `processing`.
        if (u.aplus_status !== 'processing' && u.aplus_status !== 'done' && awaitingClaim(o.id)) return o;
        if (u.aplus_status === 'done' || u.aplus_status === 'error') delete aplusStart.current[o.id];
        return { ...o, aplus_status: u.aplus_status };
      }));
    }
    const { data: rows } = await supabase.from('aplus_results').select('order_id').in('order_id', ids);
    const prog = {};
    for (const o of proc) {
      const done = (rows || []).filter(r => r.order_id === o.id).length;
      prog[o.id] = { done, total: Math.min(o.product_count || 1, 5) * 3 };
    }
    setAplusProg(prev => ({ ...prev, ...prog }));
  };

  useEffect(() => {
    const pending = () => ordersRef.current.some(
      o => (o.aplus_content || o.aplus_status === 'processing') && (o.aplus_status === 'processing' || awaitingClaim(o.id))
    );
    const tick = setInterval(() => { if (pending()) setNowTs(Date.now()); }, 1000);
    const poll = setInterval(() => { if (pending()) refreshAplus(); }, 6000);
    return () => { clearInterval(tick); clearInterval(poll); };
    // eslint-disable-next-line
  }, []);

  const handleDownload = async (order) => {
    setDownloading(order.id);
    try {
      // Token zalogowanego usera, nie anon key: edge fn sprawdza wlasciciela zlecenia (D34).
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Sesja wygasla - zaloguj sie ponownie');

      const res = await fetch(`${SUPABASE_URL}/functions/v1/get-output-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ order_id: order.id }),
      });
      if (!res.ok) throw new Error(`Błąd serwera: ${res.status}`);

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const filename = order.filename
        ? order.filename.replace(/\.xlsx$/i, '') + '_output.csv'
        : `${order.marketplace}_output.csv`;
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(`Pobrano: ${filename}`);
      await fetchOrders();
    } catch (err) {
      toast.error('Błąd pobierania: ' + err.message);
      reportAlert({ title: 'Pobieranie wynikow nie powiodlo sie', message: err.message, details: 'order=' + (order && order.id) });
    } finally {
      setDownloading(null);
    }
  };

  // Stats
  const stats = useMemo(() => {
    const total = orders.length;
    const active = orders.filter(o => ['processing', 'queued', 'pending'].includes(o.status)).length;
    const done   = orders.filter(o => ['done', 'sent'].includes(o.status)).length;
    const errors = orders.filter(o => o.status === 'error').length;
    // Liczymy to, co faktycznie trafiło do pliku (products_done), nie to, co zlecono.
    const products = orders.reduce((s, o) => s + (o.products_done ?? (['done', 'sent'].includes(o.status) ? (o.product_count || 0) : 0)), 0);
    const credits  = orders.reduce((s, o) => s + (o.credits_used || 0), 0);
    return { total, active, done, errors, products, credits };
  }, [orders]);

  // Filtered list
  const filtered = useMemo(() => {
    let list = orders;
    if (filter === 'active') list = list.filter(o => ['processing', 'queued', 'pending'].includes(o.status));
    if (filter === 'done')   list = list.filter(o => ['done', 'sent'].includes(o.status));
    if (filter === 'error')  list = list.filter(o => o.status === 'error');
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(o =>
        (o.marketplace || '').toLowerCase().includes(q) ||
        (o.language    || '').toLowerCase().includes(q) ||
        (o.email       || '').toLowerCase().includes(q) ||
        (o.filename    || '').toLowerCase().includes(q) ||
        (o.tenant_name || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [orders, filter, search]);

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Historia zleceń"
        subtitle={hideCredits ? 'Zlecenia Twojego katalogu — pobierz wyniki i sprawdź status.' : 'Wszystkie Twoje poprzednie zlecenia procesowania — pobierz wyniki, sprawdź status, przejrzyj koszty.'}
        action={
          <Button
            variant="secondary"
            size="md"
            onClick={fetchOrders}
            leftIcon={<RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />}
          >
            Odśwież
          </Button>
        }
      />

      {/* KPI strip — z motion stagger */}
      <motion.div
        className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6"
        initial="hidden"
        animate="show"
        variants={{
          hidden: {},
          show: { transition: { staggerChildren: 0.06 } },
        }}
      >
        {[
          { label: 'Łącznie zleceń',         value: formatNumber(stats.total),    icon: <Hash className="w-4 h-4" />,     tone: 'neutral' },
          { label: 'Aktywne',                 value: formatNumber(stats.active),   icon: <Activity className="w-4 h-4" />,  tone: 'brand', dot: stats.active > 0 },
          { label: 'Produkty wygenerowane',   value: formatNumber(stats.products), icon: <Sparkles className="w-4 h-4" />,  tone: 'success' },
          { label: 'Kredyty zużyte',          value: formatNumber(stats.credits),  icon: <Calendar className="w-4 h-4" />,  tone: 'neutral' },
        ].filter(k => !(hideCredits && k.label === 'Kredyty zużyte')).map((kpi, i) => (
          <motion.div
            key={i}
            variants={{
              hidden: { opacity: 0, y: 6 },
              show:   { opacity: 1, y: 0, transition: { duration: 0.28, ease: [0.4, 0, 0.2, 1] } },
            }}
          >
            <MetricCard {...kpi} />
          </motion.div>
        ))}
      </motion.div>

      {/* Filter bar */}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <Tabs
          variant="pills"
          value={filter}
          onChange={setFilter}
          tabs={FILTER_TABS.map(t => ({
            ...t,
            count:
              t.id === 'all'    ? stats.total :
              t.id === 'active' ? stats.active :
              t.id === 'done'   ? stats.done :
              t.id === 'error'  ? stats.errors : undefined,
          }))}
        />
        <div className="w-full sm:w-72">
          <Input
            type="search"
            placeholder="Szukaj marketplace, języka, e-mail…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={<Search className="w-4 h-4" />}
          />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <Card className="overflow-hidden">
          <div className="divide-y divide-ink-100 dark:divide-ink-800">
            {Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} columns={5} />)}
          </div>
        </Card>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Inbox className="w-7 h-7" />}
          title={search || filter !== 'all' ? 'Brak wyników' : 'Brak zleceń'}
          description={
            search || filter !== 'all'
              ? 'Spróbuj zmienić filtr lub frazę wyszukiwania.'
              : 'Uruchom swoje pierwsze zlecenie, aby zobaczyć je tutaj.'
          }
          action={(search || filter !== 'all') && (
            <Button variant="secondary" size="sm" onClick={() => { setSearch(''); setFilter('all'); }}>
              Wyczyść filtry
            </Button>
          )}
        />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-ink-50/80 dark:bg-ink-800/40 border-b border-ink-200 dark:border-ink-800">
                <tr>
                  {['Data', 'Marketplace', 'Język', 'Produkty', ...(hideCredits ? [] : ['Kredyty']), 'Status', ''].map(h => (
                    <th key={h} className="px-4 py-3 text-2xs font-bold uppercase tracking-wide text-ink-500 dark:text-ink-400 text-left whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100 dark:divide-ink-800">
                {filtered.map(o => (
                  <tr key={o.id} className="hover:bg-ink-50/60 dark:hover:bg-ink-800/40 transition-colors group">
                    <td className="px-4 py-3 text-xs text-ink-600 dark:text-ink-400 font-mono whitespace-nowrap">
                      {formatDateTime(o.created_at)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="inline-flex items-center gap-2">
                        <MarketplaceDot name={o.marketplace} />
                        <span className="text-sm font-semibold text-ink-900 dark:text-ink-100">{o.marketplace}</span>
                      </div>
                      {o.tenant_name && <div className="text-2xs text-ink-500 dark:text-ink-400 mt-0.5 pl-5">klient: {o.tenant_name}</div>}
                    </td>
                    <td className="px-4 py-3 text-sm text-ink-700 dark:text-ink-300 whitespace-nowrap">{o.language || '—'}</td>
                    <td className="px-4 py-3 text-sm text-ink-900 dark:text-ink-100 font-semibold whitespace-nowrap">
                      {formatNumber(o.product_count)}
                    </td>
                    {!hideCredits && (
                    <td className="px-4 py-3 text-xs text-ink-700 dark:text-ink-300 font-mono whitespace-nowrap">
                      {formatNumber(o.credits_used)}
                    </td>
                    )}
                    <td className="px-4 py-3">
                      {isCancelled(o)
                        ? <Badge variant="neutral" size="md">Anulowane</Badge>
                        : <StatusBadge status={o.status} size="md" />}
                      {o.status_reason && (
                        <div className={cn(
                          'text-xs mt-1 min-w-[220px] max-w-[420px] leading-snug',
                          isCancelled(o) ? 'text-ink-500 dark:text-ink-400'
                            : o.status === 'error' ? 'text-danger-dark dark:text-danger'
                            : 'text-warning-dark dark:text-warning'
                        )}>
                          {o.status_reason}
                        </div>
                      )}
                      {o.products_failed > 0 && o.products_done > 0 && (
                        <button
                          type="button"
                          onClick={() => openMissing(o)}
                          className="text-xs mt-1 font-semibold text-brand-700 dark:text-brand-300 hover:underline"
                        >
                          Pokaż {formatNumber(o.products_failed)} bez treści
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-2 justify-end">
                        {(o.status === 'done' || (o.status === 'error' && o.products_done > 0)) && (
                          <Button
                            size="sm"
                            variant={o.status === 'done' ? 'primary' : 'secondary'}
                            onClick={() => handleDownload(o)}
                            loading={downloading === o.id}
                            leftIcon={!downloading && <Download className="w-3.5 h-3.5" />}
                          >
                            {downloading === o.id ? 'Pobieranie' : o.status === 'done' ? 'Pobierz' : `Pobierz ${formatNumber(o.products_done)} gotowych`}
                          </Button>
                        )}
                        {/* A+ uruchomione z Gotowości nie ustawia orders.aplus_content — przycisk po aplus_status, nie po fladze z formularza. */}
                        {(o.aplus_content || o.aplus_status === 'done' || o.aplus_status === 'processing') && o.status === 'done' && !hideCredits && (
                          o.aplus_status === 'done' ? (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => openGallery(o)}
                              leftIcon={<Sparkles className="w-3.5 h-3.5" />}
                            >
                              A+ gotowe
                            </Button>
                          ) : o.aplus_status === 'processing' ? (
                            <div className="flex flex-col items-end gap-0.5">
                              <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand-600">
                                <Loader2 className="w-3.5 h-3.5 animate-spin" /> A+ w toku
                              </span>
                              <span className="text-[11px] text-ink-500 dark:text-ink-400 font-mono">
                                {aplusProg[o.id] ? `${aplusProg[o.id].done}/${aplusProg[o.id].total} grafik` : 'start…'}
                                {aplusStart.current[o.id] ? ` · ${fmtElapsed(nowTs - aplusStart.current[o.id])}` : ''}
                              </span>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => handleAplus(o)}
                              loading={aplusBusy === o.id}
                              leftIcon={!aplusBusy && <Sparkles className="w-3.5 h-3.5" />}
                            >
                              {aplusBusy === o.id ? 'Uruchamiam' : 'Generuj A+'}
                            </Button>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {!loading && filtered.length > 0 && (
        <div className="mt-3 text-xs text-ink-500 dark:text-ink-400 px-1">
          Wyświetlono {formatNumber(filtered.length)} z {formatNumber(orders.length)} zleceń
        </div>
      )}
      {missing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setMissing(null)}>
          <div className="bg-white dark:bg-ink-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[88vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-ink-100 dark:border-ink-800">
              <div>
                <h3 className="text-sm font-bold text-ink-900 dark:text-ink-100">Produkty bez treści — {missing.order.marketplace}</h3>
                <p className="text-xs text-ink-500 dark:text-ink-400 mt-0.5">
                  Tych produktów nie ma w pliku wynikowym. Kredyty za nie wróciły na konto.
                </p>
              </div>
              <Button size="sm" variant="secondary" onClick={() => setMissing(null)}>Zamknij</Button>
            </div>
            <div className="overflow-y-auto">
              {missing.loading ? (
                <div className="flex items-center justify-center py-16 text-ink-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
              ) : missing.rows.length === 0 ? (
                <div className="px-5 py-10 text-sm text-ink-500 dark:text-ink-400 text-center">
                  Brak szczegółów dla tego zlecenia (np. wiersze bez nazwy produktu w pliku).
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-ink-50/80 dark:bg-ink-800/40 border-b border-ink-200 dark:border-ink-800">
                    <tr>
                      {['Wiersz', 'Produkt', 'Powód'].map(h => (
                        <th key={h} className="px-4 py-2.5 text-2xs font-bold uppercase tracking-wide text-ink-500 dark:text-ink-400 text-left">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-100 dark:divide-ink-800">
                    {missing.rows.map(r => (
                      <tr key={r.row_index}>
                        {/* +2: wiersz 1 w pliku to nagłówek, row_index liczony od zera */}
                        <td className="px-4 py-2.5 text-xs font-mono text-ink-600 dark:text-ink-400 whitespace-nowrap">{r.row_index + 2}</td>
                        <td className="px-4 py-2.5 text-sm text-ink-900 dark:text-ink-100">{r.name || '—'}</td>
                        <td className="px-4 py-2.5 text-xs text-ink-600 dark:text-ink-400">
                          {r.reason}
                          {r.detail && <div className="mt-0.5 font-mono text-[11px] text-ink-400 break-all">{r.detail}</div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {gallery && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setGallery(null)}>
          <div className="bg-white dark:bg-ink-900 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[88vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-ink-100 dark:border-ink-800">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-brand-600" />
                <h3 className="text-sm font-bold text-ink-900 dark:text-ink-100">A+ Content — {gallery.order.marketplace}</h3>
                <span className="text-xs text-ink-500">{gallery.images.length} grafik</span>
              </div>
              <div className="flex items-center gap-2">
                {gallery.images.length > 0 && (
                  <Button size="sm" variant="primary" loading={zipBusy} onClick={() => downloadZip(gallery.order, gallery.images)} leftIcon={!zipBusy && <Download className="w-3.5 h-3.5" />}>
                    Pobierz wszystkie (ZIP)
                  </Button>
                )}
                <Button size="sm" variant="secondary" onClick={() => setGallery(null)}>Zamknij</Button>
              </div>
            </div>
            <div className="p-5 overflow-y-auto">
              {gallery.loading ? (
                <div className="flex items-center justify-center py-16 text-ink-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
              ) : gallery.images.length === 0 ? (
                <EmptyState icon={<Inbox className="w-7 h-7" />} title="Brak grafik" description="Dla tego zlecenia nie ma jeszcze wygenerowanych grafik A+." />
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {gallery.images.map((img, i) => (
                    <div key={i} className="group relative rounded-xl overflow-hidden border border-ink-100 dark:border-ink-800 bg-ink-50 dark:bg-ink-800/40">
                      <img src={img.url} alt={img.panel} loading="lazy" className="w-full aspect-square object-cover" />
                      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between px-2 py-1.5 bg-gradient-to-t from-black/70 to-transparent">
                        <span className="text-[11px] font-semibold text-white capitalize">#{img.row_index + 1} · {img.panel}</span>
                        <a href={img.url} download target="_blank" rel="noreferrer" className="text-white/90 hover:text-white" title="Pobierz">
                          <Download className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// Anulowanie zapisuje się jako status `error` z powodem „Anulowane…" (baza nie ma osobnego statusu).
function isCancelled(o) {
  return o.status === 'error' && /^Anulowane/.test(o.status_reason || '');
}

function fmtElapsed(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

function MarketplaceDot({ name }) {
  const meta = MARKETPLACE_META[name];
  if (!meta) return <span className="w-2 h-2 rounded-full bg-ink-400" />;
  return (
    <span
      className="w-2 h-2 rounded-full flex-shrink-0"
      style={{ background: meta.color }}
    />
  );
}
